export const areArraysEqual = <T>(
  array1: T[],
  array2: T[],
  comparator: (a: T | undefined, b: T | undefined) => boolean,
) => {
  if (array1 === array2) {
    return true;
  }

  const firstArrayLength = array1.length;
  const secondArrayLength = array2.length;

  if (firstArrayLength !== secondArrayLength) {
    return false;
  }

  for (let index = 0; index < firstArrayLength; index = index + 1) {
    const item1 = array1[index];
    const item2 = array2[index];

    if (!comparator(item1, item2)) {
      return false;
    }
  }

  return true;
};

const areObjectsEqual = <T extends {}>(object1: T, object2: T, comparator: <V>(a: V, b: V) => boolean) => {
  const firstObjectKeys = Object.keys(object1);
  const secondObjectKeys = Object.keys(object2);

  if (firstObjectKeys.length !== secondObjectKeys.length) {
    return false;
  }

  for (const key of firstObjectKeys) {
    const keyAsKey = key as keyof T;
    if (!comparator(object1[keyAsKey], object2[keyAsKey])) {
      return false;
    }
  }

  return true;
};

const isObjectLike = (value: unknown) => value != null && typeof value === 'object';

export const isEqual = <T>(value: T | null | undefined, other: T | null | undefined): boolean => {
  if (value === other) {
    return true;
  }

  if (Array.isArray(value) && Array.isArray(other)) {
    return areArraysEqual(value, other, isEqual);
  }

  const isFirstObject = isObjectLike(value);
  const isSecondObject = isObjectLike(other);

  if (isFirstObject && isSecondObject) {
    if (value instanceof Date || other instanceof Date) {
      return value === other;
    }

    return areObjectsEqual(value, other, isEqual);
  }

  if (typeof value === 'function' || typeof other === 'function') {
    return value === other;
  }

  return value === other;
};
